import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/movies';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setSearchOpen(false);
    }
  };

  const isHome = location.pathname === '/';

  return (
    <header className="w-full">
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || !isHome
            ? 'bg-background-100/95 backdrop-blur-md border-b border-background-200/60'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <Link
              to="/"
              className="flex items-center gap-2 whitespace-nowrap"
            >
              <div className="w-9 h-9 flex items-center justify-center bg-primary-500 rounded-lg">
                <i className="ri-movie-2-line text-lg text-background-50"></i>
              </div>
              <span className="text-xl font-heading font-bold text-foreground-950 tracking-wide">
                天美影视
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-1">
              {categories.slice(0, 6).map((cat) => (
                <Link
                  key={cat.id}
                  to={cat.id === 'all' ? '/' : `/category/${cat.id}`}
                  className={`px-3 py-1.5 rounded-full text-sm transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    (cat.id === 'all' && location.pathname === '/') ||
                    location.pathname === `/category/${cat.id}`
                      ? 'bg-primary-500/15 text-primary-500 font-medium'
                      : 'text-foreground-500 hover:text-foreground-800 hover:bg-background-200/60'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
              <div className="relative group">
                <button className="px-3 py-1.5 rounded-full text-sm text-foreground-500 hover:text-foreground-800 hover:bg-background-200/60 transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center gap-1">
                  更多 <i className="ri-arrow-down-s-line text-xs"></i>
                </button>
                <div className="absolute top-full left-0 mt-1 bg-background-100 border border-background-200/60 rounded-xl p-2 min-w-[160px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  {categories.slice(6).map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.id}`}
                      className="block px-4 py-2 text-sm text-foreground-500 hover:text-foreground-800 hover:bg-background-200/60 rounded-lg transition-all duration-150 whitespace-nowrap"
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="relative">
                {searchOpen && (
                  <form
                    onSubmit={handleSearch}
                    className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center"
                  >
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="搜索影视..."
                      className="w-48 bg-background-200/80 border border-background-300/60 text-foreground-800 text-sm rounded-lg px-3 py-1.5 outline-none focus:border-primary-500/50 transition-all"
                      autoFocus
                    />
                  </form>
                )}
              </div>
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                className="w-9 h-9 flex items-center justify-center rounded-lg text-foreground-500 hover:text-foreground-800 hover:bg-background-200/60 transition-all cursor-pointer"
                aria-label="搜索"
              >
                <i className={`text-lg ${searchOpen ? 'ri-close-line' : 'ri-search-line'}`}></i>
              </button>

              <Link
                to="/about"
                className="hidden md:flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm text-foreground-500 hover:text-foreground-800 hover:bg-background-200/60 transition-all whitespace-nowrap"
              >
                <i className="ri-information-line"></i> 关于我们
              </Link>

              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg text-foreground-500 hover:text-foreground-800 hover:bg-background-200/60 transition-all cursor-pointer"
                aria-label="菜单"
              >
                <i className={`text-lg ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
        </div>

        {mobileOpen && (
          <div className="md:hidden bg-background-100/95 backdrop-blur-md border-t border-background-200/60 px-4 py-4 animate-in slide-in-from-top-2 duration-200">
            <div className="grid grid-cols-3 gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={cat.id === 'all' ? '/' : `/category/${cat.id}`}
                  className={`px-3 py-2 rounded-lg text-sm text-center transition-all cursor-pointer ${
                    (cat.id === 'all' && location.pathname === '/') ||
                    location.pathname === `/category/${cat.id}`
                      ? 'bg-primary-500/15 text-primary-500 font-medium'
                      : 'text-foreground-500 hover:bg-background-200/60'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
            <Link
              to="/about"
              className="block mt-3 px-4 py-2.5 rounded-lg text-sm text-center text-foreground-500 hover:bg-background-200/60 transition-all border border-background-200/60"
            >
              <i className="ri-information-line mr-1"></i> 关于我们
            </Link>
          </div>
        )}
      </nav>
    </header>
  );
}